import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from '../../i18n';

interface HealthMetrics {
  date: string;
  recovery: {
    score: number;
    status: 'Red' | 'Yellow' | 'Green';
    recommendation: string;
  };
  sleep: {
    score: number;
    totalSleep: number;
    efficiency: number;
    deepPercent: number;
    remPercent: number;
    awakenings: number;
  };
  strain: {
    score: number;
    category: string;
    totalSteps: number;
  };
}

interface SleepStageSegment {
  startTime: string;
  endTime: string;
  stage: 'awake' | 'rem' | 'light' | 'deep';
  durationMinutes: number;
}

interface SleepStagesData {
  sleepStart: string;
  sleepEnd: string;
  stages: SleepStageSegment[];
  summary: {
    awake: number;
    rem: number;
    light: number;
    deep: number;
  };
}

interface SleepRecommendations {
  optimalBedtime: string;
  recommendedWakeTime: string;
  idealDuration: string;
  deepSleepStatus: 'low' | 'good' | 'excellent';
  remSleepStatus: 'low' | 'good' | 'excellent';
  avgDeepPercent: number;
  avgRemPercent: number;
  consistencyMinutes: number;
  consistencyStatus: 'poor' | 'good';
}

interface RecoveryPrediction {
  predictedScore: number;
  status: 'Red' | 'Yellow' | 'Green';
  confidence: 'high' | 'medium' | 'low';
  factors: {
    estimatedSleep: number;
    todayStrainImpact: number;
    sleepDebtImpact: number;
    consistencyBonus: number;
  };
  recommendation: string;
}

interface HealthMetricsViewProps {
  userId?: string;
  token?: string;
}

// Day selector styled components
const DateHeader = styled.div`
  text-align: center;
  padding: 0 16px;
`;

const DayName = styled.h1`
  font-size: 20px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
`;

const DateText = styled.p`
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 2px 0 0 0;
`;

const DaySelectorContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 16px 4px;
  gap: 12px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const DayItem = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  transition: transform 0.2s ease;

  &:active {
    transform: scale(0.95);
  }
`;

const DayRing = styled.div<{ $selected: boolean }>`
  width: 44px;
  height: 44px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ $selected }) => $selected && `
    &::after {
      content: '';
      position: absolute;
      inset: -3px;
      border: 2px solid var(--color-border);
      border-radius: 50%;
    }
  `}
`;

const DayLabel = styled.span<{ $selected: boolean }>`
  font-size: 12px;
  font-weight: 500;
  color: ${({ $selected }) => $selected ? 'var(--color-text)' : 'var(--color-text-muted)'};
`;

const NoDataCircle = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(138, 99, 210, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-primary);
  font-size: 18px;
  font-weight: 600;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 8px 0;
  width: 100%;
  max-width: 375px;
  margin: 0 auto;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.h2`
  font-size: 28px;
  font-weight: 600;
  color: var(--color-text);
  text-align: center;
  margin: 0;
`;

const CircularProgressContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 16px 0;
`;

const CircularProgress = styled.div<{ $percentage: number; $color: string }>`
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    transform: rotate(-90deg);
    width: 100%;
    height: 100%;
  }

  circle {
    fill: none;
    stroke-width: 12;
  }

  .bg-circle {
    stroke: var(--color-border-subtle);
  }

  .progress-circle {
    stroke: ${({ $color }) => $color};
    stroke-linecap: round;
    stroke-dasharray: ${({ $percentage }) => {
      const circumference = 2 * Math.PI * 88; // radius = 88
      return `${(circumference * $percentage) / 100} ${circumference}`;
    }};
    transition: stroke-dasharray 1s ease-in-out;
  }
`;

const ScoreContent = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
`;

const ScoreValue = styled.div`
  font-size: 64px;
  font-weight: 700;
  color: var(--color-text);
  line-height: 1;
`;

const ScoreLabel = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text);
  text-transform: capitalize;
`;

const StatusBadge = styled.div<{ $status: 'Red' | 'Yellow' | 'Green' }>`
  font-size: 15px;
  font-weight: 600;
  color: ${({ $status }) => {
    if ($status === 'Green') return '#4CAF50';
    if ($status === 'Yellow') return '#FF9800';
    return '#F44336';
  }};
  padding: 8px 20px;
  background: ${({ $status }) => {
    if ($status === 'Green') return 'rgba(76, 175, 80, 0.15)';
    if ($status === 'Yellow') return 'rgba(255, 152, 0, 0.15)';
    return 'rgba(244, 67, 54, 0.15)';
  }};
  border-radius: 100px;
  margin-top: 8px;
`;

const InfoText = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: var(--color-text);
  text-align: center;
  margin: 0;
  padding: 0 24px;
`;

const InsightBox = styled.div`
  background: var(--color-bg-elevated);
  border-radius: 12px;
  padding: 14px 16px;
  margin: 0 16px;
`;

const InsightText = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: var(--color-text);
  margin: 0;
`;

const DetailsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0 16px;
`;

const DetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  background: var(--color-bg-elevated);
  border-radius: 10px;
`;

const DetailLabel = styled.div`
  font-size: 14px;
  color: var(--color-text-secondary);
`;

const DetailValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  gap: 16px;
  color: var(--color-text-secondary);
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid var(--color-border-subtle);
  border-top-color: var(--color-primary);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 32px;
  text-align: center;
  color: var(--color-text-secondary);
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 20px;
  opacity: 0.5;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 12px 0;
`;

const EmptyDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: var(--color-text-secondary);
  margin: 0;
`;

// Sleep Stages Chart Styled Components
const SleepChartContainer = styled.div`
  margin: 8px 16px 0;
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 16px 12px;
`;

const SleepChartTitle = styled.div`
  font-size: 14px;
  color: var(--color-text-secondary);
  text-align: center;
  margin-bottom: 12px;
`;

const SleepChartSVG = styled.svg`
  width: 100%;
  height: 120px;
  display: block;
`;

const SleepTimeAxis = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 4px 0;
`;

const SleepTimeLabel = styled.span<{ $highlight?: boolean }>`
  font-size: 11px;
  color: ${({ $highlight }) => ($highlight ? 'var(--color-text)' : 'var(--color-text-muted)')};
  background: ${({ $highlight }) => ($highlight ? 'var(--color-border-subtle)' : 'transparent')};
  padding: ${({ $highlight }) => ($highlight ? '2px 6px' : '0')};
  border-radius: 4px;
`;

const SleepLegend = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--color-border-subtle);
`;

const SleepLegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SleepLegendColor = styled.div<{ $color: string }>`
  width: 16px;
  height: 8px;
  background: ${({ $color }) => $color};
  border-radius: 2px;
`;

const SleepLegendLabel = styled.span`
  font-size: 13px;
  color: var(--color-text);
  flex: 1;
`;

const SleepLegendValue = styled.span`
  font-size: 13px;
  color: var(--color-text-secondary);
`;

// Sleep Recommendations Card
const RecommendationsCard = styled.div`
  background: linear-gradient(135deg, rgba(33, 150, 243, 0.15) 0%, rgba(103, 58, 183, 0.15) 100%);
  border: 1px solid rgba(33, 150, 243, 0.2);
  border-radius: 16px;
  padding: 16px;
  margin: 16px 16px 0;
`;

const RecommendationsTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const RecommendationsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;
`;

const RecommendationItem = styled.div`
  text-align: center;
`;

const RecommendationValue = styled.div`
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text);
  margin-bottom: 2px;
`;

const RecommendationLabel = styled.div`
  font-size: 11px;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const QualitySection = styled.div`
  border-top: 1px solid var(--color-border-subtle);
  padding-top: 12px;
`;

const QualitySectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 8px;
`;

const QualityRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 0;
`;

const QualityLabel = styled.span`
  font-size: 13px;
  color: var(--color-text);
`;

const QualityValue = styled.span`
  font-size: 13px;
  color: var(--color-text);
`;

const QualityStatus = styled.span<{ $status: 'low' | 'good' | 'excellent' | 'poor' }>`
  font-size: 12px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: 8px;
  background: ${({ $status }) => {
    if ($status === 'excellent') return 'rgba(76, 175, 80, 0.2)';
    if ($status === 'good') return 'rgba(33, 150, 243, 0.2)';
    if ($status === 'poor') return 'rgba(255, 152, 0, 0.2)';
    return 'rgba(255, 152, 0, 0.2)';
  }};
  color: ${({ $status }) => {
    if ($status === 'excellent') return '#4CAF50';
    if ($status === 'good') return '#2196F3';
    if ($status === 'poor') return '#FF9800';
    return '#FF9800';
  }};
`;

// Recovery Prediction Card
const PredictionCard = styled.div`
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(59, 130, 246, 0.15) 100%);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 16px;
  padding: 16px;
  margin: 16px 16px 0;
`;

const PredictionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const PredictionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConfidenceBadge = styled.span<{ $confidence: 'high' | 'medium' | 'low' }>`
  font-size: 11px;
  font-weight: 500;
  padding: 3px 8px;
  border-radius: 10px;
  background: ${({ $confidence }) => {
    if ($confidence === 'high') return 'rgba(76, 175, 80, 0.2)';
    if ($confidence === 'medium') return 'rgba(255, 193, 7, 0.2)';
    return 'rgba(158, 158, 158, 0.2)';
  }};
  color: ${({ $confidence }) => {
    if ($confidence === 'high') return '#4CAF50';
    if ($confidence === 'medium') return '#FFC107';
    return '#9E9E9E';
  }};
`;

const PredictionScore = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 12px 0;
`;

const PredictionValue = styled.div<{ $status: 'Red' | 'Yellow' | 'Green' }>`
  font-size: 48px;
  font-weight: 700;
  color: ${({ $status }) => {
    if ($status === 'Green') return '#4CAF50';
    if ($status === 'Yellow') return '#FF9800';
    return '#F44336';
  }};
`;

const PredictionUnit = styled.span`
  font-size: 24px;
  font-weight: 400;
  color: var(--color-text-secondary);
`;

const PredictionStatus = styled.div<{ $status: 'Red' | 'Yellow' | 'Green' }>`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $status }) => {
    if ($status === 'Green') return '#4CAF50';
    if ($status === 'Yellow') return '#FF9800';
    return '#F44336';
  }};
`;

const PredictionFactors = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--color-border-subtle);
`;

const PredictionFactor = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const FactorLabel = styled.span`
  font-size: 11px;
  color: var(--color-text-secondary);
  text-transform: uppercase;
`;

const FactorValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text);
`;

const PredictionRecommendation = styled.div`
  margin-top: 12px;
  padding: 10px 12px;
  background: var(--color-bg-elevated);
  border-radius: 10px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--color-text);
`;

// Health Goals Section
const GoalsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 0 16px;
`;

const GoalsSectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
  margin: 0 0 4px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GoalCard = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  background: ${({ $color }) => `linear-gradient(135deg, ${$color}20 0%, ${$color}10 100%)`};
  border: 1px solid ${({ $color }) => `${$color}30`};
  border-radius: 14px;
`;

const GoalIcon = styled.div<{ $color: string }>`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${({ $color }) => `${$color}25`};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
`;

const GoalContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const GoalLabel = styled.span`
  font-size: 12px;
  color: var(--color-text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const GoalValue = styled.span`
  font-size: 20px;
  font-weight: 700;
  color: var(--color-text);
`;

const GoalProgress = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const GoalProgressText = styled.span<{ $achieved: boolean }>`
  font-size: 13px;
  font-weight: 600;
  color: ${({ $achieved }) => $achieved ? '#4CAF50' : 'var(--color-text)'};
`;

const GoalProgressBar = styled.div`
  width: 60px;
  height: 6px;
  background: var(--color-border-subtle);
  border-radius: 3px;
  overflow: hidden;
`;

const GoalProgressFill = styled.div<{ $percent: number; $color: string }>`
  width: ${({ $percent }) => Math.min($percent, 100)}%;
  height: 100%;
  background: ${({ $color }) => $color};
  border-radius: 3px;
  transition: width 0.5s ease;
`;

// Sleep stage colors
const STAGE_COLORS = {
  awake: '#F5F5F5',
  rem: '#64B5F6',
  light: '#1E88E5',
  deep: '#0D47A1',
};

// Sleep stage Y positions (percentage from top)
const STAGE_Y_POSITIONS = {
  awake: 10,
  rem: 35,
  light: 60,
  deep: 85,
};

const getStatusColor = (status: 'Red' | 'Yellow' | 'Green'): string => {
  if (status === 'Green') return '#4CAF50';
  if (status === 'Yellow') return '#FF9800';
  return '#F44336';
};

// getStatusText, DAY_NAMES, MONTH_NAMES are now provided via i18n inside the component
const DAY_ABBREVS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

// Get last 7 days
const getLast7Days = (): Date[] => {
  const days: Date[] = [];
  for (let i = 6; i >= 0; i--) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    date.setHours(0, 0, 0, 0);
    days.push(date);
  }
  return days;
};

// Format date as YYYY-MM-DD
const formatDateKey = (date: Date): string => {
  // Use local date to avoid timezone issues
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Parse date string safely
const parseDateSafe = (dateStr: string | Date): Date => {
  if (dateStr instanceof Date) {
    return dateStr;
  }
  // If it's an ISO string with timezone (e.g., "2026-01-14T21:00:00.000Z"),
  // parse it fully to get the correct local date
  if (String(dateStr).includes('T')) {
    const date = new Date(dateStr);
    // Return a new date at midnight local time
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }
  // Handle YYYY-MM-DD format
  const cleanDate = String(dateStr).split('T')[0];
  const [year, month, day] = cleanDate.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Mini Activity Rings Component
const MiniActivityRings: React.FC<{ metrics: HealthMetrics }> = ({ metrics }) => {
  const recoveryPercent = metrics.recovery.score / 100;
  const sleepPercent = metrics.sleep.score / 100;
  const strainPercent = metrics.strain.score / 21;

  const circumference = 2 * Math.PI * 18;

  return (
    <svg width="44" height="44" viewBox="0 0 44 44">
      {/* Outer ring - Recovery (light purple) */}
      <circle cx="22" cy="22" r="18" fill="none" stroke="rgba(193, 159, 236, 0.2)" strokeWidth="4" />
      <circle
        cx="22"
        cy="22"
        r="18"
        fill="none"
        stroke="#C19FEC"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={`${circumference * recoveryPercent} ${circumference}`}
        transform="rotate(-90 22 22)"
      />
      {/* Middle ring - Sleep (light blue) */}
      <circle cx="22" cy="22" r="13" fill="none" stroke="rgba(127, 169, 246, 0.2)" strokeWidth="4" />
      <circle
        cx="22"
        cy="22"
        r="13"
        fill="none"
        stroke="#7FA9F6"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={`${2 * Math.PI * 13 * sleepPercent} ${2 * Math.PI * 13}`}
        transform="rotate(-90 22 22)"
      />
      {/* Inner ring - Strain (light lavender) */}
      <circle cx="22" cy="22" r="8" fill="none" stroke="rgba(210, 205, 255, 0.2)" strokeWidth="4" />
      <circle
        cx="22"
        cy="22"
        r="8"
        fill="none"
        stroke="#D2CDFF"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray={`${2 * Math.PI * 8 * strainPercent} ${2 * Math.PI * 8}`}
        transform="rotate(-90 22 22)"
      />
    </svg>
  );
};

// Helper to format minutes as "Xh Ym"
const formatDuration = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  if (hours > 0 && mins > 0) return `${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h`;
  return `${mins}m`;
};

// Helper to format time for display (12h format)
const formatTimeDisplay = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number);
  const period = hours >= 12 ? 'pm' : 'am';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
};

// Calculate sleep recommendations from metrics and sleep stages
const calculateSleepRecommendations = (
  metricsArray: HealthMetrics[],
  sleepStagesMap: Map<string, SleepStagesData>
): SleepRecommendations | null => {
  if (metricsArray.length === 0) return null;

  // Collect bedtimes and their corresponding sleep scores
  const bedtimeData: Array<{ bedtime: string; score: number }> = [];

  sleepStagesMap.forEach((stages, dateKey) => {
    if (stages.sleepStart) {
      const metric = metricsArray.find(m => {
        const metricDate = new Date(m.date).toISOString().split('T')[0];
        const stageDate = new Date(dateKey).toISOString().split('T')[0];
        return metricDate === stageDate;
      });
      if (metric) {
        bedtimeData.push({ bedtime: stages.sleepStart, score: metric.sleep.score });
      }
    }
  });

  if (bedtimeData.length === 0) return null;

  // Find optimal bedtime from best nights
  const sortedByScore = [...bedtimeData].sort((a, b) => b.score - a.score);
  const topNights = sortedByScore.slice(0, Math.min(3, sortedByScore.length));

  // Calculate average bedtime of best nights
  const bedtimeMinutes = topNights.map(n => {
    const [hours, mins] = n.bedtime.split(':').map(Number);
    return hours < 12 ? (hours + 24) * 60 + mins : hours * 60 + mins;
  });

  const avgMinutes = Math.round(bedtimeMinutes.reduce((a, b) => a + b, 0) / bedtimeMinutes.length);
  const optimalHour = Math.floor(avgMinutes / 60) % 24;
  const optimalMin = avgMinutes % 60;
  const optimalBedtime = `${optimalHour.toString().padStart(2, '0')}:${optimalMin.toString().padStart(2, '0')}`;

  // Calculate average sleep duration
  const avgSleepDuration = metricsArray.reduce((sum, m) => sum + m.sleep.totalSleep, 0) / metricsArray.length;
  const idealDuration = avgSleepDuration >= 7 ? avgSleepDuration.toFixed(1) : '7.5';

  // Calculate recommended wake time
  const wakeMinutes = avgMinutes + Math.round(parseFloat(idealDuration) * 60);
  const wakeHour = Math.floor(wakeMinutes / 60) % 24;
  const wakeMin = wakeMinutes % 60;
  const recommendedWakeTime = `${wakeHour.toString().padStart(2, '0')}:${wakeMin.toString().padStart(2, '0')}`;

  // Analyze deep sleep and REM
  const avgDeep = Math.round(metricsArray.reduce((sum, m) => sum + m.sleep.deepPercent, 0) / metricsArray.length);
  const avgREM = Math.round(metricsArray.reduce((sum, m) => sum + m.sleep.remPercent, 0) / metricsArray.length);

  const deepSleepStatus: 'low' | 'good' | 'excellent' = avgDeep < 13 ? 'low' : avgDeep > 23 ? 'excellent' : 'good';
  const remSleepStatus: 'low' | 'good' | 'excellent' = avgREM < 20 ? 'low' : avgREM > 25 ? 'excellent' : 'good';

  // Calculate bedtime consistency
  const allBedtimeMinutes = bedtimeData.map(b => {
    const [h, m] = b.bedtime.split(':').map(Number);
    return h < 12 ? (h + 24) * 60 + m : h * 60 + m;
  });
  const mean = allBedtimeMinutes.reduce((a, b) => a + b, 0) / allBedtimeMinutes.length;
  const variance = allBedtimeMinutes.reduce((sum, m) => sum + Math.pow(m - mean, 2), 0) / allBedtimeMinutes.length;
  const consistencyMinutes = Math.round(Math.sqrt(variance));

  return {
    optimalBedtime,
    recommendedWakeTime,
    idealDuration,
    deepSleepStatus,
    remSleepStatus,
    avgDeepPercent: avgDeep,
    avgRemPercent: avgREM,
    consistencyMinutes,
    consistencyStatus: consistencyMinutes > 60 ? 'poor' : 'good',
  };
};

export const HealthMetricsView: React.FC<HealthMetricsViewProps> = ({ userId, token }) => {
  const { t, tArray } = useTranslation();
  const DAY_NAMES = tArray('health.dayNames');
  const MONTH_NAMES = tArray('health.monthNames');

  const getStatusText = (status: 'Red' | 'Yellow' | 'Green'): string => {
    if (status === 'Green') return t('health.statusOptimal');
    if (status === 'Yellow') return t('health.statusGood');
    return t('health.statusPoor');
  };

  const [metricsMap, setMetricsMap] = useState<Map<string, HealthMetrics>>(new Map());
  const [sleepStagesMap, setSleepStagesMap] = useState<Map<string, SleepStagesData>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [hasData, setHasData] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [last7Days] = useState<Date[]>(getLast7Days());
  const [sleepRecommendations, setSleepRecommendations] = useState<SleepRecommendations | null>(null);
  const [recoveryPrediction, setRecoveryPrediction] = useState<RecoveryPrediction | null>(null);
  const [dailyStepTarget, setDailyStepTarget] = useState<number>(10000);

  // Get metrics for selected date
  const selectedDateKey = formatDateKey(selectedDate);
  const metrics = metricsMap.get(selectedDateKey) || null;
  const sleepStages = sleepStagesMap.get(selectedDateKey) || null;

  useEffect(() => {
    const controller = new AbortController();

    const fetchHealthMetrics = async () => {
      if (!userId || !token) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const apiUrl = import.meta.env.VITE_API_URL || '/api';
        // Get user's timezone offset in minutes (negate because JS returns opposite sign)
        // For UTC+3 (Turkey), getTimezoneOffset() returns -180, we need +180
        const timezoneOffset = -new Date().getTimezoneOffset();
        const response = await fetch(
          `${apiUrl}/chat/health-metrics/${userId}?token=${encodeURIComponent(token)}&tz=${timezoneOffset}`,
          {
            method: 'GET',
            credentials: 'include',
            signal: controller.signal,
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (controller.signal.aborted) return;

          if (data.success && data.data?.hasData) {
            // Build metrics map from trends
            const newMap = new Map<string, HealthMetrics>();

            if (data.data.trends && Array.isArray(data.data.trends)) {
              data.data.trends.forEach((m: HealthMetrics) => {
                // Handle date format (could be Date object or string)
                const dateKey = formatDateKey(parseDateSafe(m.date));
                newMap.set(dateKey, m);
              });
            }

            // Also add latest if not in trends
            if (data.data.latest) {
              const latestKey = formatDateKey(parseDateSafe(data.data.latest.date));
              if (!newMap.has(latestKey)) {
                newMap.set(latestKey, data.data.latest);
              }
            }

            setMetricsMap(newMap);

            // Build sleep stages map
            if (data.data.sleepStages && typeof data.data.sleepStages === 'object') {
              const stagesMap = new Map<string, SleepStagesData>();
              Object.entries(data.data.sleepStages).forEach(([dateKey, stagesData]) => {
                // Convert ISO date key to local date key
                const localDateKey = formatDateKey(parseDateSafe(dateKey));
                stagesMap.set(localDateKey, stagesData as SleepStagesData);
              });
              setSleepStagesMap(stagesMap);
            }

            setHasData(newMap.size > 0);

            // Calculate sleep recommendations from all metrics
            if (data.data.sleepStages) {
              const stagesMapForCalc = new Map<string, SleepStagesData>();
              Object.entries(data.data.sleepStages).forEach(([dateKey, stagesData]) => {
                stagesMapForCalc.set(dateKey, stagesData as SleepStagesData);
              });
              const metricsArray = Array.from(newMap.values());
              const recommendations = calculateSleepRecommendations(metricsArray, stagesMapForCalc);
              setSleepRecommendations(recommendations);

              // Calculate daily step target based on average (round to nearest 1000, minimum 8000)
              if (metricsArray.length > 0) {
                const avgSteps = metricsArray.reduce((sum, m) => sum + m.strain.totalSteps, 0) / metricsArray.length;
                const target = Math.max(8000, Math.round(avgSteps / 1000) * 1000 + 2000);
                setDailyStepTarget(target);
              }
            }

            // Fetch recovery prediction for tomorrow
            try {
              const predictionResponse = await fetch(
                `${apiUrl}/health/prediction/${userId}`,
                {
                  method: 'GET',
                  credentials: 'include',
                  signal: controller.signal,
                }
              );
              if (predictionResponse.ok) {
                const predictionData = await predictionResponse.json();
                if (controller.signal.aborted) return;
                if (predictionData.success && predictionData.data?.tomorrow) {
                  setRecoveryPrediction(predictionData.data.tomorrow);
                }
              }
            } catch (predictionError) {
              // Prediction is optional enrichment; the view renders without it.
              if (!controller.signal.aborted) {
                console.warn('[HealthMetrics] Prediction fetch failed:', predictionError);
              }
            }

            // Set selected date to the most recent day with data
            const today = formatDateKey(new Date());
            if (newMap.has(today)) {
              setSelectedDate(new Date());
            } else if (data.data.latest) {
              setSelectedDate(parseDateSafe(data.data.latest.date));
            }
          } else {
            setHasData(false);
          }
        } else {
          console.error('[HealthMetrics] Request failed with status', response.status);
          setHasData(false);
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('[HealthMetrics] Error fetching data:', error);
        setHasData(false);
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchHealthMetrics();

    return () => controller.abort();
  }, [userId, token]);

  if (isLoading) {
    return (
      <Container>
        <LoadingContainer>
          <Spinner />
          <div>{t('health.loading')}</div>
        </LoadingContainer>
      </Container>
    );
  }

  if (!hasData || !metrics) {
    return (
      <Container>
        <EmptyState>
          <EmptyIcon>⌚</EmptyIcon>
          <EmptyTitle>{t('health.noData')}</EmptyTitle>
          <EmptyDescription>
            {t('health.noDataDescription')}
          </EmptyDescription>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      {/* Date Header */}
      <DateHeader>
        <DayName>{DAY_NAMES[selectedDate.getDay()]}</DayName>
        <DateText>{MONTH_NAMES[selectedDate.getMonth()]} {selectedDate.getDate()}, {selectedDate.getFullYear()}</DateText>
      </DateHeader>

      {/* 7-Day Selector */}
      <DaySelectorContainer>
        {last7Days.map((day) => {
          const dateKey = formatDateKey(day);
          const dayMetrics = metricsMap.get(dateKey);
          const isSelected = dateKey === selectedDateKey;

          return (
            <DayItem
              key={dateKey}
              type="button"
              $selected={isSelected}
              onClick={() => setSelectedDate(day)}
              aria-label={`${DAY_NAMES[day.getDay()] || ''} ${day.getDate()}`}
            >
              <DayRing $selected={isSelected}>
                {dayMetrics ? (
                  <MiniActivityRings metrics={dayMetrics} />
                ) : (
                  <NoDataCircle>✕</NoDataCircle>
                )}
              </DayRing>
              <DayLabel $selected={isSelected}>
                {DAY_ABBREVS[day.getDay()]}
              </DayLabel>
            </DayItem>
          );
        })}
      </DaySelectorContainer>

      {/* Show message if no data for selected day */}
      {!metrics && (
        <EmptyState>
          <EmptyIcon>📊</EmptyIcon>
          <EmptyTitle>{t('health.noDataForDay')}</EmptyTitle>
          <EmptyDescription>
            {t('health.noDataForDayDescription', { day: DAY_NAMES[selectedDate.getDay()] || '' })}
          </EmptyDescription>
        </EmptyState>
      )}

      {/* Recovery Section */}
      {metrics && (
      <Section>
        <SectionTitle>{t('health.recovery')}</SectionTitle>
        <CircularProgressContainer>
          <CircularProgress
            $percentage={metrics.recovery.score}
            $color={getStatusColor(metrics.recovery.status)}
          >
            <svg viewBox="0 0 200 200">
              <circle className="bg-circle" cx="100" cy="100" r="88" />
              <circle className="progress-circle" cx="100" cy="100" r="88" />
            </svg>
            <ScoreContent>
              <ScoreValue>{metrics.recovery.score}</ScoreValue>
            </ScoreContent>
          </CircularProgress>
          <StatusBadge $status={metrics.recovery.status}>
            {getStatusText(metrics.recovery.status)}
          </StatusBadge>
        </CircularProgressContainer>

        <InsightBox>
          <InsightText>{metrics.recovery.recommendation}</InsightText>
        </InsightBox>

        {/* Recovery Prediction Card */}
        {recoveryPrediction && (
          <PredictionCard>
            <PredictionHeader>
              <PredictionTitle>
                <span>📈</span> {t('health.tomorrowForecast')}
              </PredictionTitle>
              <ConfidenceBadge $confidence={recoveryPrediction.confidence}>
                {recoveryPrediction.confidence === 'high' ? t('health.highConfidence') :
                 recoveryPrediction.confidence === 'medium' ? t('health.mediumConfidence') : t('health.lowConfidence')}
              </ConfidenceBadge>
            </PredictionHeader>

            <PredictionScore>
              <PredictionValue $status={recoveryPrediction.status}>
                ~{recoveryPrediction.predictedScore}
                <PredictionUnit>%</PredictionUnit>
              </PredictionValue>
              <PredictionStatus $status={recoveryPrediction.status}>
                {recoveryPrediction.status === 'Green' ? `🟢 ${t('health.statusOptimal')}` :
                 recoveryPrediction.status === 'Yellow' ? `🟡 ${t('health.statusGood')}` : `🔴 ${t('health.statusPoor')}`}
              </PredictionStatus>
            </PredictionScore>

            <PredictionFactors>
              <PredictionFactor>
                <FactorLabel>{t('health.estSleep')}</FactorLabel>
                <FactorValue>{recoveryPrediction.factors.estimatedSleep}%</FactorValue>
              </PredictionFactor>
              <PredictionFactor>
                <FactorLabel>{t('health.strainImpact')}</FactorLabel>
                <FactorValue>+{recoveryPrediction.factors.todayStrainImpact}</FactorValue>
              </PredictionFactor>
              <PredictionFactor>
                <FactorLabel>{t('health.sleepDebt')}</FactorLabel>
                <FactorValue>+{recoveryPrediction.factors.sleepDebtImpact}</FactorValue>
              </PredictionFactor>
              <PredictionFactor>
                <FactorLabel>{t('health.consistency')}</FactorLabel>
                <FactorValue>+{recoveryPrediction.factors.consistencyBonus}</FactorValue>
              </PredictionFactor>
            </PredictionFactors>

            <PredictionRecommendation>
              💡 {recoveryPrediction.recommendation}
            </PredictionRecommendation>
          </PredictionCard>
        )}
      </Section>
      )}

      {/* Sleep Section */}
      {metrics && (
      <Section>
        <SectionTitle>{t('health.sleep')}</SectionTitle>
        <CircularProgressContainer>
          <CircularProgress $percentage={metrics.sleep.score} $color="#2196F3">
            <svg viewBox="0 0 200 200">
              <circle className="bg-circle" cx="100" cy="100" r="88" />
              <circle className="progress-circle" cx="100" cy="100" r="88" />
            </svg>
            <ScoreContent>
              <ScoreValue>{metrics.sleep.score}</ScoreValue>
            </ScoreContent>
          </CircularProgress>
        </CircularProgressContainer>

        <InfoText>
          {t('health.sleptInfo', { hours: Math.floor(metrics.sleep.totalSleep), minutes: Math.round((metrics.sleep.totalSleep % 1) * 60) })}
        </InfoText>

        <DetailsList>
          <DetailItem>
            <DetailLabel>{t('health.efficiency')}</DetailLabel>
            <DetailValue>{metrics.sleep.efficiency}%</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>{t('health.deepSleep')}</DetailLabel>
            <DetailValue>{metrics.sleep.deepPercent}%</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>{t('health.remSleep')}</DetailLabel>
            <DetailValue>{metrics.sleep.remPercent}%</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>{t('health.awakenings')}</DetailLabel>
            <DetailValue>{metrics.sleep.awakenings}</DetailValue>
          </DetailItem>
        </DetailsList>

        {/* Sleep Stages Chart */}
        {sleepStages && sleepStages.stages.length > 0 && (
          <SleepChartContainer>
            <SleepChartTitle>{t('health.sleepCycles')}</SleepChartTitle>
            <SleepChartSVG viewBox="0 0 300 100" preserveAspectRatio="none">
              {/* Background grid lines */}
              <line x1="0" y1="10" x2="300" y2="10" stroke="var(--color-border-subtle)" strokeWidth="0.5" />
              <line x1="0" y1="35" x2="300" y2="35" stroke="var(--color-border-subtle)" strokeWidth="0.5" />
              <line x1="0" y1="60" x2="300" y2="60" stroke="var(--color-border-subtle)" strokeWidth="0.5" />
              <line x1="0" y1="85" x2="300" y2="85" stroke="var(--color-border-subtle)" strokeWidth="0.5" />

              {/* Sleep stages path */}
              {(() => {
                const totalMinutes = sleepStages.stages.reduce((sum, s) => sum + s.durationMinutes, 0);
                let currentX = 0;
                const pathSegments: JSX.Element[] = [];

                sleepStages.stages.forEach((segment, index) => {
                  const segmentWidth = (segment.durationMinutes / totalMinutes) * 300;
                  const y = STAGE_Y_POSITIONS[segment.stage];
                  const nextSegment = sleepStages.stages[index + 1];
                  const nextY = nextSegment ? STAGE_Y_POSITIONS[nextSegment.stage] : y;

                  // Horizontal line for this segment
                  pathSegments.push(
                    <line
                      key={`h-${index}`}
                      x1={currentX}
                      y1={y}
                      x2={currentX + segmentWidth}
                      y2={y}
                      stroke={STAGE_COLORS[segment.stage]}
                      strokeWidth="3"
                    />
                  );

                  // Vertical line to next segment (if different)
                  if (nextSegment && nextY !== y) {
                    pathSegments.push(
                      <line
                        key={`v-${index}`}
                        x1={currentX + segmentWidth}
                        y1={y}
                        x2={currentX + segmentWidth}
                        y2={nextY}
                        stroke={STAGE_COLORS[segment.stage]}
                        strokeWidth="2"
                        opacity="0.6"
                      />
                    );
                  }

                  currentX += segmentWidth;
                });

                return pathSegments;
              })()}
            </SleepChartSVG>

            <SleepTimeAxis>
              <SleepTimeLabel $highlight>{formatTimeDisplay(sleepStages.sleepStart)}</SleepTimeLabel>
              <SleepTimeLabel $highlight>{formatTimeDisplay(sleepStages.sleepEnd)}</SleepTimeLabel>
            </SleepTimeAxis>

            <SleepLegend>
              <SleepLegendItem>
                <SleepLegendColor $color={STAGE_COLORS.awake} />
                <SleepLegendLabel>{t('health.awake')}</SleepLegendLabel>
                <SleepLegendValue>{formatDuration(sleepStages.summary.awake)}</SleepLegendValue>
              </SleepLegendItem>
              <SleepLegendItem>
                <SleepLegendColor $color={STAGE_COLORS.rem} />
                <SleepLegendLabel>{t('health.rem')}</SleepLegendLabel>
                <SleepLegendValue>{formatDuration(sleepStages.summary.rem)}</SleepLegendValue>
              </SleepLegendItem>
              <SleepLegendItem>
                <SleepLegendColor $color={STAGE_COLORS.light} />
                <SleepLegendLabel>{t('health.light')}</SleepLegendLabel>
                <SleepLegendValue>{formatDuration(sleepStages.summary.light)}</SleepLegendValue>
              </SleepLegendItem>
              <SleepLegendItem>
                <SleepLegendColor $color={STAGE_COLORS.deep} />
                <SleepLegendLabel>{t('health.deep')}</SleepLegendLabel>
                <SleepLegendValue>{formatDuration(sleepStages.summary.deep)}</SleepLegendValue>
              </SleepLegendItem>
            </SleepLegend>
          </SleepChartContainer>
        )}

        {/* Sleep Recommendations Card */}
        {sleepRecommendations && (
          <RecommendationsCard>
            <RecommendationsTitle>
              <span>💤</span> {t('health.sleepRecommendations')}
            </RecommendationsTitle>

            <RecommendationsGrid>
              <RecommendationItem>
                <RecommendationValue>{formatTimeDisplay(sleepRecommendations.optimalBedtime)}</RecommendationValue>
                <RecommendationLabel>{t('health.bedtime')}</RecommendationLabel>
              </RecommendationItem>
              <RecommendationItem>
                <RecommendationValue>{formatTimeDisplay(sleepRecommendations.recommendedWakeTime)}</RecommendationValue>
                <RecommendationLabel>{t('health.wakeTime')}</RecommendationLabel>
              </RecommendationItem>
              <RecommendationItem>
                <RecommendationValue>{sleepRecommendations.idealDuration}h</RecommendationValue>
                <RecommendationLabel>{t('health.idealDuration')}</RecommendationLabel>
              </RecommendationItem>
            </RecommendationsGrid>

            <QualitySection>
              <QualitySectionTitle>{t('health.sleepQualityAnalysis')}</QualitySectionTitle>
              <QualityRow>
                <QualityLabel>{t('health.deepSleep')}</QualityLabel>
                <QualityValue>
                  {sleepRecommendations.avgDeepPercent}%
                  <QualityStatus $status={sleepRecommendations.deepSleepStatus}>
                    {sleepRecommendations.deepSleepStatus === 'excellent' ? t('health.excellent') :
                     sleepRecommendations.deepSleepStatus === 'good' ? t('health.statusGood') : t('health.low')}
                  </QualityStatus>
                </QualityValue>
              </QualityRow>
              <QualityRow>
                <QualityLabel>{t('health.remSleep')}</QualityLabel>
                <QualityValue>
                  {sleepRecommendations.avgRemPercent}%
                  <QualityStatus $status={sleepRecommendations.remSleepStatus}>
                    {sleepRecommendations.remSleepStatus === 'excellent' ? t('health.excellent') :
                     sleepRecommendations.remSleepStatus === 'good' ? t('health.statusGood') : t('health.low')}
                  </QualityStatus>
                </QualityValue>
              </QualityRow>
              <QualityRow>
                <QualityLabel>{t('health.consistency')}</QualityLabel>
                <QualityValue>
                  ±{sleepRecommendations.consistencyMinutes} min
                  <QualityStatus $status={sleepRecommendations.consistencyStatus}>
                    {sleepRecommendations.consistencyStatus === 'good' ? t('health.statusGood') : t('health.variable')}
                  </QualityStatus>
                </QualityValue>
              </QualityRow>
            </QualitySection>
          </RecommendationsCard>
        )}
      </Section>
      )}

      {/* Strain/Activity Section */}
      {metrics && (
      <Section>
        <SectionTitle>{t('health.activity')}</SectionTitle>
        <CircularProgressContainer>
          <CircularProgress
            $percentage={(metrics.strain.score / 21) * 100}
            $color="#FF6B00"
          >
            <svg viewBox="0 0 200 200">
              <circle className="bg-circle" cx="100" cy="100" r="88" />
              <circle className="progress-circle" cx="100" cy="100" r="88" />
            </svg>
            <ScoreContent>
              <ScoreValue>{metrics.strain.score.toFixed(1)}</ScoreValue>
              <ScoreLabel>{metrics.strain.category}</ScoreLabel>
            </ScoreContent>
          </CircularProgress>
        </CircularProgressContainer>

        <InfoText>
          {t('health.walkedSteps', { steps: metrics.strain.totalSteps.toLocaleString() })}
        </InfoText>

        <DetailsList>
          <DetailItem>
            <DetailLabel>{t('health.category')}</DetailLabel>
            <DetailValue>{metrics.strain.category}</DetailValue>
          </DetailItem>
          <DetailItem>
            <DetailLabel>{t('health.score')}</DetailLabel>
            <DetailValue>{metrics.strain.score.toFixed(1)} / 21</DetailValue>
          </DetailItem>
        </DetailsList>
      </Section>
      )}

      {/* Daily Goals Section */}
      {metrics && (sleepRecommendations || dailyStepTarget) && (
        <Section>
          <GoalsSection>
            <GoalsSectionTitle>
              <span>🎯</span> {t('health.dailyGoals')}
            </GoalsSectionTitle>

            {/* Bedtime Goal */}
            {sleepRecommendations && (
              <GoalCard $color="#7FA9F6">
                <GoalIcon $color="#7FA9F6">🌙</GoalIcon>
                <GoalContent>
                  <GoalLabel>{t('health.bedtime')}</GoalLabel>
                  <GoalValue>{formatTimeDisplay(sleepRecommendations.optimalBedtime)}</GoalValue>
                </GoalContent>
              </GoalCard>
            )}

            {/* Daily Steps Goal */}
            <GoalCard $color="#FF6B00">
              <GoalIcon $color="#FF6B00">👟</GoalIcon>
              <GoalContent>
                <GoalLabel>{t('health.dailySteps')}</GoalLabel>
                <GoalValue>{dailyStepTarget.toLocaleString()}</GoalValue>
              </GoalContent>
              <GoalProgress>
                <GoalProgressText $achieved={metrics.strain.totalSteps >= dailyStepTarget}>
                  {metrics.strain.totalSteps >= dailyStepTarget ? `✓ ${t('health.done')}` : `${metrics.strain.totalSteps.toLocaleString()}`}
                </GoalProgressText>
                <GoalProgressBar>
                  <GoalProgressFill
                    $percent={(metrics.strain.totalSteps / dailyStepTarget) * 100}
                    $color="#FF6B00"
                  />
                </GoalProgressBar>
              </GoalProgress>
            </GoalCard>
          </GoalsSection>
        </Section>
      )}
    </Container>
  );
};
